import {
  differenceInMilliseconds,
  format,
  isAfter,
  isValid,
  parseISO,
  toDate,
  isSameDay,
  differenceInMonths,
  differenceInDays,
  startOfDay,
  addMonths,
  subMinutes
} from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import { DateInputType } from '@webthatmatters/orca-dateinput';
import _findKey from 'lodash/findKey';

/* 
Note on timezone handling:
When dealing with date strings that do not include timezone information (e.g., "2025-12-22 14:30:00"),
it's crucial to avoid unintentional timezone conversions that can lead to incorrect date/time values.

The following examples illustrate common pitfalls and correct approaches:
// ❌ WRONG: Parses as local, then converts to UTC (shifts time)
const localDate = parseWithoutTimezoneConversion("2025-12-22 14:30:00");
formatInTimeZone(localDate, 'UTC', 'yyyy-MM-dd HH:mm:ss');
// In EST (UTC-5): displays "2025-12-22 19:30:00" ⚠️ Shifted!

// ✅ CORRECT: Parse as UTC, then format in UTC (no shift)
const utcDate = parseISO("2025-12-22T14:30:00Z"); // Note the "Z"
formatInTimeZone(utcDate, 'UTC', 'yyyy-MM-dd HH:mm:ss');
// Displays: "2025-12-22 14:30:00" ✅ 

// ✅ ALTERNATIVE: Parse locally, format locally (no timezone involvement)
const localDate = parseWithoutTimezoneConversion("2025-12-22 14:30:00");
format(localDate, 'yyyy-MM-dd HH:mm:ss');
// Displays: "2025-12-22 14:30:00" ✅
*/

type HideType = {
  time?: boolean;
  date?: boolean;
  day?: boolean;
  month?: boolean;
  year?: boolean;
};

const defs = {
  year: 'yyyy',
  month: 'MM',
  day: 'dd'
};

export const getDefaultFormat = (hide?: HideType) => {
  const dateFormat = 'dd/MM/yyyy';
  const timeFormat = 'HH:mm';
  let format = '';

  if (hide) {
    let formated_date = '';

    if (!hide.date) {
      // Checks for YYYY, MM, DD
      let parts = dateFormat.split('/');
      parts = parts.filter(part => {
        const foundKey = _findKey(defs, def => def === part) as keyof HideType | undefined;

        if (foundKey) {
          return !hide[foundKey];
        }

        return true;
      });

      if (parts.length) {
        formated_date = parts.join('/');
      }
    }

    if (!hide.time) {
      format = `${formated_date !== '' ? `${formated_date}, ` : ''}${timeFormat}`;
    } else {
      format = `${formated_date}`;
    }
  } else {
    format = `${dateFormat}, ${timeFormat}`;
  }

  return format;
};

export const getIsValidDateObject = (date?: Date | string | null): date is Date => {
  if (isValid(date) && date instanceof Date) return true;

  return false;
};

const getParsedDate = (date?: string | null) => {
  if (typeof date === 'string') {
    const parsedDate = parseISO(date);

    if (!isValid(parsedDate)) return null;

    return parsedDate;
  }

  return null;
};

// Displays dates always in UTC
export const displayUTCDate = (
  date?: Date | string | null,
  hide: HideType = {},
  customFormat?: string
) => {
  if (getIsValidDateObject(date))
    return formatInTimeZone(date, 'UTC', customFormat ?? getDefaultFormat(hide));

  const parsedDate = getParsedDate(date);

  if (!parsedDate) return null;

  return formatInTimeZone(parsedDate, 'UTC', customFormat ?? getDefaultFormat(hide));
};

const parseWithoutTimezoneConversion = (dateString: string): Date | null => {
  // Handle formats: "2025-12-22T02:30:00.000000Z", "2025-12-22 02:30:00", "2025-12-22"
  // Remove microseconds and Z suffix if present (e.g., ".000000Z")
  const cleanString = dateString
    .replace(/\.\d+Z?$/, '')
    .replace('Z', '')
    .replace('T', ' ');

  // Extract date/time components: "YYYY-MM-DD" with optional " HH:MM:SS"
  const match = cleanString.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}):(\d{2}):?(\d{2})?)?$/);

  if (!match) return null;

  const [, year, month, day, hour = '0', minute = '0', second = '0'] = match;

  // Validate ranges before creating Date object
  const y = parseInt(year);
  const m = parseInt(month);
  const d = parseInt(day);
  const h = parseInt(hour);
  const min = parseInt(minute);
  const s = parseInt(second);

  // Check for valid ranges
  if (m < 1 || m > 12) return null;
  if (d < 1 || d > 31) return null;
  if (h < 0 || h > 23) return null;
  if (min < 0 || min > 59) return null;
  if (s < 0 || s > 59) return null;

  // Create a date using the components as-is (no timezone conversion)
  const date = new Date(y, m - 1, d, h, min, s);

  // Verify the date components match what we intended (catches invalid dates like Feb 31)
  if (
    date.getFullYear() !== y ||
    date.getMonth() !== m - 1 ||
    date.getDate() !== d ||
    date.getHours() !== h ||
    date.getMinutes() !== min ||
    date.getSeconds() !== s
  ) {
    return null;
  }

  if (!isValid(date)) return null;

  return date;
};

// Displays date without timezone conversion for string dates
export const displayDate = (
  date?: Date | string | null,
  hide: HideType = {},
  customFormat?: string
) => {
  if (getIsValidDateObject(date)) return format(date, customFormat ?? getDefaultFormat(hide));

  // Use timezone-agnostic parsing for string dates
  if (typeof date === 'string') {
    const parsedDate = parseWithoutTimezoneConversion(date);
    if (!parsedDate) return null;

    return format(parsedDate, customFormat ?? getDefaultFormat(hide));
  }

  return null;
};

export const getClosestNextDate = (
  datesArray: (string | Date)[],
  comparisonDate?: string | Date,
  includeToday = true
) => {
  const now = comparisonDate
    ? typeof comparisonDate === 'string'
      ? parseISO(comparisonDate)
      : comparisonDate
    : new Date();

  const futureDates = datesArray.filter(date => {
    const compareDate = typeof date === 'string' ? parseISO(date) : date;

    if (includeToday) {
      return isAfter(compareDate, now) || isSameDay(compareDate, now);
    }

    return isAfter(compareDate, now);
  });

  if (!futureDates.length) return null;

  const closestDate = futureDates.reduce<Date | null>((prev, curr) => {
    const currentDate = typeof curr === 'string' ? parseISO(curr) : curr;

    if (!prev) return currentDate;

    const diffPrev = differenceInMilliseconds(prev, now);
    const diffCurr = differenceInMilliseconds(currentDate, now);
    return diffCurr < diffPrev ? currentDate : prev;
  }, null);

  return closestDate;
};

export const getDateObject = (dateString: string | null) => {
  if (dateString) {
    // Check if the date string is in the format "YYYY-MM-DD HH:MM:SS" without a timezone.
    const dateWithoutTimezone = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/;

    if (dateWithoutTimezone.test(dateString)) {
      // Append "Z" to indicate UTC.
      return toDate(`${dateString}Z`);
    }

    return toDate(dateString);
  }

  return null;
};

// Convert wall-clock local_timestamp + timezone offset (hours) to UTC, e.g. 12:00 at +3 → 09:00 UTC.
export const getUtcTimestampFromLocal = (
  localTimestamp: DateInputType,
  timezoneHours: number
): string | null => {
  if (!localTimestamp || !isValid(localTimestamp)) return null;

  const localWallClock = formatInTimeZone(localTimestamp, 'UTC', 'yyyy-MM-dd HH:mm:ss');
  const wallClockDate = getDateObject(localWallClock);

  if (!wallClockDate || !isValid(wallClockDate)) return null;

  const utcDate = subMinutes(wallClockDate, timezoneHours * 60);

  return formatInTimeZone(utcDate, 'UTC', 'yyyy-MM-dd HH:mm:ss');
};

export const getMonthAndDaysDurationString = (date: string) => {
  const startDate = startOfDay(parseISO(date));
  const today = startOfDay(new Date());

  const months = differenceInMonths(today, startDate);

  // Calculate the start date after subtracting  months
  const adjustedStartDate = addMonths(startDate, months);

  // Calculate the remaining days between today and the adjusted start date
  const days = differenceInDays(today, adjustedStartDate);

  return `${months > 0 ? (months !== 1 ? `${months} months ` : '1 month ') : ''}${
    days > 0 && months > 0 ? ' ' : ''
  }${days > 0 ? (days !== 1 ? `${days} days` : '1 day') : ''}`.trim();
};

export const convertLocalDateToUTC = (dateString: string, timeZoneOffset: number): string => {
  const date = getDateObject(dateString);

  if (!date) return '';

  // Convert to UTC by subtracting the timezone offset in minutes
  const utcDate = subMinutes(date, timeZoneOffset * 60);

  // Return UTC date in ISO format
  return utcDate.toISOString();
};

const MILLISECONDS_IN_HOUR = 60 * 60 * 1000;

const getReferenceDate = (date?: Date | string | null): Date => {
  if (getIsValidDateObject(date)) return date;

  if (typeof date === 'string') {
    const parsedDate = getParsedDate(date);
    if (parsedDate) return parsedDate;
  }

  return new Date();
};

// Convert an IANA timezone (e.g. "Europe/Athens") to UTC offset hours (e.g. 3, -5, 5.5).
// Pass `date` so DST is applied for that instant; otherwise the current date is used.
export const getUtcOffsetHoursFromTimezone = (
  timezone?: string | null,
  date?: Date | string | null
): number | null => {
  if (!timezone) return null;

  const trimmedTimezone = timezone.trim();
  if (!trimmedTimezone) return null;

  const offsetMs = getTimezoneOffset(trimmedTimezone, getReferenceDate(date));
  if (!Number.isFinite(offsetMs)) return null;

  return Number((offsetMs / MILLISECONDS_IN_HOUR).toFixed(2));
};

export const formatUtcOffsetHours = (offsetHours: number | null): string | null => {
  if (offsetHours === null || !Number.isFinite(offsetHours)) return null;

  return offsetHours >= 0 ? `+${offsetHours}` : `${offsetHours}`;
};

export const isValidDateFormat = (dateString: string | null): boolean => {
  if (!dateString) return false;

  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

export const showFormattedDurationBetweenDates = ({
  from,
  to
}: {
  from: DateInputType | string;
  to: DateInputType | string;
}) => {
  if (!from || !to) return '';

  const startDate = typeof from === 'string' ? parseISO(from) : from;
  const endDate = typeof to === 'string' ? parseISO(to) : to;

  const totalMilliseconds = differenceInMilliseconds(endDate, startDate);
  const totalDays = totalMilliseconds / (1000 * 60 * 60 * 24);

  // Calculate hours and minutes from total milliseconds
  const totalHours = Math.floor(totalMilliseconds / (1000 * 60 * 60));
  const remainingMinutes = Math.floor((totalMilliseconds % (1000 * 60 * 60)) / (1000 * 60));

  const parts = [];
  if (totalHours) parts.push(`${totalHours}h`);
  if (remainingMinutes) parts.push(`${remainingMinutes}m`);

  const formattedDuration = parts.join(' ');
  const decimalDays = totalDays.toFixed(6);

  return `${formattedDuration} – ${decimalDays} days`;
};
